import type { ComponentProps, ReactNode } from 'react';
import React from 'react';
import Loading from '../../loading';
import type {
  BaseProps,
  ComponentAppearance,
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
import {
  baseInputClasses,
  getInputAppearanceClasses,
  inputSizeClasses,
  getDisabledClasses,
} from 'src/modules/shared/styles/component.styles';

interface SelectOption {
  content: ReactNode | string;
  value: string;
}

interface SelectProps
  extends Omit<BaseProps<ComponentProps<'select'>>, 'size' | 'variant'> {
  options: SelectOption[];
  size?: ComponentSize;
  variant?: ComponentVariant;
  appearance?: ComponentAppearance;
  containerClassName?: string;
}

const Select: React.FC<SelectProps> = ({
  options,
  variant = 'primary',
  appearance = 'outline',
  size = 'md',
  loading = false,
  disabled = false,
  className = '',
  containerClassName = '',
  ...rest
}) => {
  const iconBackgroundStyle = loading
    ? undefined
    : ({
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%239CA3AF' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")",
        backgroundPosition: 'right 0.5rem center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '1rem 1rem',
      } satisfies React.CSSProperties);

  const classes = [
    'appearance-none',
    ...baseInputClasses,
    getInputAppearanceClasses(variant, appearance),
    inputSizeClasses[size],
    'pr-8',
    getDisabledClasses(disabled || loading),
    className,
  ].join(' ');

  return (
    <div className={`relative w-full ${containerClassName}`}>
      <select
        className={classes}
        disabled={disabled || loading}
        style={{
          ...iconBackgroundStyle,
          ...rest.style,
        }}
        {...rest}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.content}
          </option>
        ))}
      </select>
      {loading ? (
        <div className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center">
          <Loading className="w-4 h-4" />
        </div>
      ) : null}
    </div>
  );
};

export default Select;
